import { useEffect, useState } from "react";
import { Mail, RefreshCw, Unplug, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* Connect Gmail — bookings appear automatically, no forwarding. Hidden until the
   owner configures GOOGLE_CLIENT_ID/SECRET in the backend environment. */
export default function GmailSyncCard({ onImported }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/gmail/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const q = new URLSearchParams(window.location.search).get("gmail");
    if (q === "connected") toast.success("Gmail connected — we'll scan for bookings automatically every night");
    if (q === "error" || q === "denied") toast.error("Gmail connection didn't complete — try again");
  }, []);
  if (!st?.configured) return null;

  const connect = async () => {
    try { const { data } = await api.get("/gmail/connect"); window.location.href = data.url; }
    catch { toast.error("Could not start the Gmail connection"); }
  };
  const scan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/gmail/scan", {}, { timeout: 120000 });
      toast.success(data.queued ? `${data.queued} booking email${data.queued > 1 ? "s" : ""} found — review below`
        : "No new booking emails found");
      onImported?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Gmail? Automatic booking capture will stop.")) return;
    try { await api.delete("/gmail/disconnect"); load(); toast.success("Gmail disconnected"); } catch {}
  };

  return (
    <div className="border border-zinc-300 bg-white mb-6 p-4 flex flex-wrap items-center gap-3" data-testid="gmail-sync-card">
      <Mail className="w-6 h-6 text-rose-600 shrink-0" />
      <div className="flex-1 min-w-[220px]">
        <div className="font-heading font-700">Gmail Inbox Sync</div>
        {st.connected ? (
          <div className="text-xs text-emerald-700 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> Connected: {st.email}
            {st.last_scan && <span className="text-muted-foreground"> · last scan {new Date(st.last_scan).toLocaleString()}</span>}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">Connect Gmail and booking confirmations appear here automatically — no forwarding needed. Read-only access; disconnect anytime.</div>
        )}
      </div>
      {st.connected ? (
        <div className="flex gap-2">
          <button onClick={scan} disabled={busy} data-testid="gmail-scan-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-rose-600 text-white text-xs font-700 hover:bg-rose-500 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Scanning…" : "Scan inbox now"}
          </button>
          <button onClick={disconnect} data-testid="gmail-disconnect-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-300 text-zinc-600 text-xs font-700 hover:bg-zinc-50">
            <Unplug className="w-4 h-4" /> Disconnect
          </button>
        </div>
      ) : (
        <button onClick={connect} data-testid="gmail-connect-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-rose-600 text-white text-sm font-700 hover:bg-rose-500">
          <Mail className="w-4 h-4" /> Connect Gmail
        </button>
      )}
    </div>
  );
}
